import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  ShowButton,
  TopToolbar,
  FilterButton,
  TextInput,
  SelectInput,
  NumberField,
  useListContext,
} from 'react-admin';
import { AccountStatusDto, AccountTypeDto } from 'src/dtos';
import { AccountStatusChip, PhoneNumberArrayField } from 'src/lib';
import { AccountStatus, AccountStatusLabel, AccountTypeLabel, BaseAccount } from 'src/models';

import { SyncAccountListStatusButton } from './SyncAccountListStatusButton';

export const AccountListFilters = [
  <TextInput key='accountId' label='Account Id' source='accountId' />,
  <TextInput key='accountNumber' label='Account number' source='accountNumber' alwaysOn />,
  <SelectInput
    key='type'
    label='Type'
    source='type'
    choices={[
      { id: AccountTypeDto.Individual, name: AccountTypeLabel.Individual },
      { id: AccountTypeDto.Joint, name: AccountTypeLabel.Joint },
      { id: AccountTypeDto.Retirement, name: AccountTypeLabel.Retirement },
      { id: AccountTypeDto.Ugma, name: AccountTypeLabel.Ugma },
      { id: AccountTypeDto.Entity, name: AccountTypeLabel.Entity },
    ]}
  />,
  <TextInput key='firstName' label='First name' source='firstName' />,
  <TextInput key='lastName' label='Last name' source='lastName' />,
  <TextInput key='email' label='Email' source='email' />,
  <TextInput key='phoneNumber' label='Phone number' source='phoneNumber' />,
  <SelectInput
    key='status'
    label='Status'
    source='status'
    choices={[
      { id: AccountStatusDto.AccountSetup, name: AccountStatusLabel.AccountSetup },
      { id: AccountStatusDto.ActionRequired, name: AccountStatusLabel.ActionRequired },
      { id: AccountStatusDto.BackOffice, name: AccountStatusLabel.BackOffice },
      { id: AccountStatusDto.InvestigationSubmitted, name: AccountStatusLabel.InvestigationSubmitted },
      { id: AccountStatusDto.New, name: AccountStatusLabel.New },
      { id: AccountStatusDto.Pending, name: AccountStatusLabel.Pending },
      { id: AccountStatusDto.PendingSubmit, name: AccountStatusLabel.PendingSubmit },
      { id: AccountStatusDto.ReadyForBackOffice, name: AccountStatusLabel.ReadyForBackOffice },
      { id: AccountStatusDto.Complete, name: AccountStatusLabel.Complete },
      { id: AccountStatusDto.Suspended, name: AccountStatusLabel.Suspended },
      { id: AccountStatusDto.Canceled, name: AccountStatusLabel.Canceled },
      { id: AccountStatusDto.Error, name: AccountStatusLabel.Error },
      { id: AccountStatusDto.Rejected, name: AccountStatusLabel.Rejected },
      { id: 'NotSubmitted', name: AccountStatusLabel.NotSubmitted },
    ]}
    optionText={(record: { id: AccountStatusDto; name: AccountStatusLabel }) => (
      <AccountStatusChip value={new AccountStatus(record.id)} />
    )}
  />,
];

export const AccountListActions = () => {
  const { data } = useListContext<BaseAccount>();

  return (
    <TopToolbar>
      <SyncAccountListStatusButton ids={data?.map(account => account.id)} />
      <FilterButton />
    </TopToolbar>
  );
};

export const AccountList = () => (
  <List
    title='Accounts'
    filters={AccountListFilters}
    sort={{ field: 'createdAt', order: 'DESC' }}
    actions={<AccountListActions />}>
    <Datagrid bulkActionButtons={false}>
      <TextField source='id' label='Id' sortable={false} />
      <TextField source='accountId' label='Account Id' />
      <TextField source='accountNumber' label='Account number' />
      <FunctionField
        label='Type'
        render={(account: BaseAccount) => {
          if (account?.type.isRetirement) {
            return account.retirementAccountType?.label;
          }

          if (account?.type.isEntity) {
            return account.entityAccountType?.label;
          }

          return account?.type.label;
        }}
      />
      <TextField source='primaryAccountHolder.firstName' label='First Name' />
      <TextField source='primaryAccountHolder.middleName' label='Middle Name' sortable={false} />
      <TextField source='primaryAccountHolder.lastName' label='Last Name' />
      <TextField source='primaryAccountHolder.suffix' label='Suffix' sortable={false} />
      <TextField source='primaryAccountHolder.email' label='Email' />
      <FunctionField
        render={(record: BaseAccount) => {
          return <PhoneNumberArrayField record={record.primaryAccountHolder?.phones} />;
        }}
        label='Phone Number'
      />
      <FunctionField
        label='Status'
        render={(account: BaseAccount) => {
          if (account.status) {
            return <AccountStatusChip value={account.status} />;
          }

          return null;
        }}
      />
      <NumberField
        source='cashBalance'
        label='Cash balance'
        options={{ style: 'currency', currency: 'USD' }}
        sortable={false}
      />
      <DateField source='createdAt' label='Created At' />
      <ShowButton label='Show' />
    </Datagrid>
  </List>
);
